<script lang="ts">
	import { createEventDispatcher, onMount } from 'svelte';
	import { format } from 'svelte-i18n';

	import {
		Alert,
		AlertV2,
		Button,
		ButtonV2,
		Container,
		Icon,
		IconButton,
		Label,
		LabelV2,
		Modal,
		Stack,
		Text,
		toast,
	} from '@Shelly/shelly-design-system';

	import {
		CAMERA_CLOUD_STORAGE_GRADIENT,
		buildSlots,
		getOccupiedCameraIds,
		getSlotCountsFromSettings,
		setCameraSlotList,
	} from 'src/helpers/camera_cloud_storage_helpers';
	import { camera_cloud_storage } from 'src/stores/features/cameras/cloud-storage';
	import { user_data, user_settings } from 'src/stores/account/user';
	import { devices } from 'src/stores/lists/devices';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { shellyId16 } from 'src/helpers/interface_helpers';
	import { getDeviceImage } from 'src/helpers/device_state_view_helpers';
	import type { Device } from 'src/types/Device';
	import type {
		CameraCloudStorageActivePlan,
		CameraCloudStorageSlot,
	} from 'src/types/CameraCloudStorage';

	import SelectCameraForSlotPicker from './SelectCameraForSlotPicker.svelte';

	/**
	 * Manage Camera Cloud Storage modal — entry point for assigning,
	 * replacing or removing cameras occupying Cloud Storage slots.
	 *
	 * The component supports two visual layers:
	 *   - Main view: slot list + Add camera CTA.
	 *   - Picker view: house-grouped camera picker for either adding
	 *     new cameras (multi-select) or replacing the camera in a
	 *     specific slot (single-select).
	 *
	 * On Save the modal POSTs the flattened device-id list to Spas's
	 * `POST /user/set_camera_list` endpoint. The BE owns the
	 * `c:{email}:list` SET — slot indices are FE-only ordering used to
	 * label Premium vs subscription rows.
	 */
	export let showModal = false;
	export let activePlan: CameraCloudStorageActivePlan | null = null;
	/** Open the modal directly on the Add-camera step (used by the post-subscribe wizard). */
	export let openOnAddStep = false;
	/**
	 * When provided, the modal opens with this camera already placed in
	 * the first empty slot (draft-only — nothing is saved until the user
	 * clicks "Save"). Used by the camera side-panel promo banner so the
	 * user doesn't have to manually pick the camera they're already
	 * looking at.
	 */
	export let preselectedCameraId: string | undefined;

	const dispatch = createEventDispatcher<{
		close: void;
		upgrade: void;
	}>();

	type View = 'main' | 'add' | 'replace';

	let view: View = 'main';
	let editingSlotIndex: number | null = null;
	let pickerSelection: string[] = [];
	let draftAssignments: Record<number, string | null> = {};
	let saving = false;

	$: ButtonComp = $showNewLayout ? ButtonV2 : Button;

	$: isPremium = ($user_data?.premium ?? 0) > 0;
	/** Total slot count from `camera_slots.all`; `isPremium` only drives the first-slot source label. */
	$: totalSlots = getSlotCountsFromSettings($user_settings).all;
	$: slots = buildSlots(activePlan, isPremium, draftAssignments);
	$: usedSlots = slots.filter((slot) => slot.cameraId).length;
	$: freeSlotsCount = Math.max(0, totalSlots - usedSlots);
	$: occupiedIds = getOccupiedCameraIds(draftAssignments);
	$: premiumOnlyNoPlans = isPremium && !activePlan;
	$: hasDirtyChanges = !isSameAssignments(draftAssignments, $camera_cloud_storage.slotAssignments);
	$: replaceInitialSelection =
		editingSlotIndex != null && draftAssignments[editingSlotIndex]
			? [draftAssignments[editingSlotIndex] as string]
			: ([] as string[]);

	onMount(() => {
		draftAssignments = { ...$camera_cloud_storage.slotAssignments };
		if (openOnAddStep) {
			view = 'add';
			pickerSelection = [];
		}
		if (preselectedCameraId) {
			const currentSlots = buildSlots(activePlan, isPremium, draftAssignments);
			const alreadyAssigned = currentSlots.some((s) => s.cameraId === preselectedCameraId);
			if (!alreadyAssigned) {
				const firstEmpty = currentSlots.find((s) => !s.cameraId);
				if (firstEmpty) {
					draftAssignments = { ...draftAssignments, [firstEmpty.index]: preselectedCameraId };
				}
			}
		}
	});

	function isSameAssignments(
		a: Record<number, string | null>,
		b: Record<number, string | null>,
	): boolean {
		const aKeys = Object.keys(a);
		const bKeys = Object.keys(b);
		if (aKeys.length !== bKeys.length) return false;
		for (const key of aKeys) {
			if (a[Number(key)] !== b[Number(key)]) return false;
		}
		return true;
	}

	/**
	 * Look up a device from `$devices` by camera ID. The `camera_list`
	 * from the backend stores decimal device IDs, but `$devices` is
	 * keyed by hex IDs (via `convert_array_to_object(devs, 'id')`).
	 * Try the raw key first, then fall back to `shellyId16(cameraId)`
	 * which converts decimal → padded hex.
	 */
	function resolveDevice(cameraId: string | null): Device | undefined {
		if (!cameraId) return undefined;
		return ($devices?.[cameraId] ?? $devices?.[shellyId16(cameraId)]) as Device | undefined;
	}

	function getCameraName(cameraId: string | null): string {
		if (!cameraId) return '';
		return resolveDevice(cameraId)?.name ?? cameraId;
	}

	function getCameraDevice(cameraId: string | null): Device | undefined {
		return resolveDevice(cameraId);
	}

	function startAdd() {
		view = 'add';
		pickerSelection = [];
	}

	function startReplace(slot: CameraCloudStorageSlot) {
		editingSlotIndex = slot.index;
		pickerSelection = slot.cameraId ? [slot.cameraId] : [];
		view = 'replace';
	}

	function removeFromSlot(slot: CameraCloudStorageSlot) {
		if (!slot.cameraId) return;
		const next = { ...draftAssignments };
		delete next[slot.index];
		draftAssignments = next;
	}

	function onPickerChange(event: CustomEvent<{ cameraIds: string[] }>) {
		pickerSelection = event.detail.cameraIds;
	}

	function applyAdd() {
		const next = { ...draftAssignments };
		const emptyIndexes = slots.filter((s) => !s.cameraId).map((s) => s.index);
		pickerSelection.forEach((cameraId, i) => {
			const slotIndex = emptyIndexes[i];
			if (slotIndex == null) return;
			next[slotIndex] = cameraId;
		});
		draftAssignments = next;
		view = 'main';
		pickerSelection = [];
	}

	/** Applies the picked camera to `editingSlotIndex` and removes it from any other slot. */
	function applyReplace() {
		if (editingSlotIndex == null) return;
		const next = { ...draftAssignments };
		if (pickerSelection.length === 0) {
			delete next[editingSlotIndex];
		} else {
			const [newCameraId] = pickerSelection;
			for (const key of Object.keys(next)) {
				if (next[Number(key)] === newCameraId) delete next[Number(key)];
			}
			next[editingSlotIndex] = newCameraId;
		}
		draftAssignments = next;
		view = 'main';
		editingSlotIndex = null;
		pickerSelection = [];
	}

	function cancelPicker() {
		if (openOnAddStep) {
			handleClose();
			return;
		}
		view = 'main';
		editingSlotIndex = null;
		pickerSelection = [];
	}

	function handleClose() {
		showModal = false;
		dispatch('close');
	}

	/**
	 * The Modal's "X" / backdrop close event should be context-aware:
	 *
	 *   - When opened via the post-subscribe wizard (`openOnAddStep`),
	 *     closing always dismisses the modal entirely.
	 *   - In the Add / Replace picker sub-views we bounce back to the
	 *     main slot list so the user doesn't lose draft assignments.
	 *   - On the main view it closes the modal.
	 */
	function handleModalClose() {
		if (openOnAddStep || view === 'main') {
			handleClose();
			return;
		}
		cancelPicker();
	}

	/**
	 * POSTs occupied slot ids to `set_camera_list`. On success updates
	 * the store to match `draftAssignments` until the next
	 * `user_settings` hydration.
	 */
	async function handleSave() {
		if (saving) return;
		saving = true;

		const deviceIds = Array.from(getOccupiedCameraIds(draftAssignments));

		const result = await setCameraSlotList(deviceIds);
		saving = false;

		if (result.error) {
			toast.open({
				type: 'danger',
				title: $format(result.error.key, {
					default: result.error.default,
					values: { ids: result.error.ids?.join(', ') ?? '' },
				}),
			});
			return;
		}

		camera_cloud_storage.setAssignments(draftAssignments);
		toast.open({
			type: 'success',
			title: $format('toast_camera_cloud_storage_assignments_saved', {
				default: 'Camera Cloud Storage slots updated.',
			}),
		});
		handleClose();
	}

	$: modalTitle =
		view === 'add'
			? $format('modal_title_fill_empty_camera_slots', {
					default: 'Fill the empty camera slots',
				})
			: view === 'replace'
				? $format('modal_title_replace_camera_in_slot', {
						default: 'Replace camera',
					})
				: $format('modal_title_manage_camera_cloud_storage', {
						default: 'Manage Camera Cloud Storage',
					});
</script>

{#if showModal}
	<Modal {showModal} on:close={handleModalClose} title={modalTitle} removeScrollPadding>
		<div slot="content" class="ccs-modal-content">
			{#if view === 'main'}
				{#if activePlan}
					<div
						class="ccs-modal-banner"
						style={`background: ${CAMERA_CLOUD_STORAGE_GRADIENT};`}
					>
						<div class="d-flex align-items-center gap-s flex-1 min-width-0">
							<div class="ccs-banner-icon">
								<Icon icon="fas fa-camera" />
							</div>
							<div class="flex-1 min-width-0">
								<Text styleType="controls-large" class="text-bold text-white-imp">
									{$format('text_camera_cloud_subscription_title', {
										default: `Camera Cloud subscription - ${activePlan.tier} Cameras`,
										values: { count: activePlan.tier },
									})}
								</Text>
								<Text styleType="body-small" class="text-white-imp text-white-imp--soft">
									{$format('text_camera_cloud_storage_usage_summary', {
										default: `You currently have are using ${usedSlots} from ${totalSlots} Camera Cloud Storage…`,
										values: { used: usedSlots, total: totalSlots },
									})}
								</Text>
							</div>
						</div>
						<ButtonV2 size="m" variant="secondary" on:click={() => dispatch('upgrade')}>
							{$format('button_subscription_upgrade', { default: 'Upgrade' })}
						</ButtonV2>
					</div>
				{:else if isPremium}
					{#if $showNewLayout}
						<AlertV2
							class="mb-m"
							heading={$format('alert_camera_cloud_storage_premium_only_heading', {
								default:
									'You have 1 Camera Cloud Storage slot included in your Premium subscription',
							})}
							message={$format('alert_camera_cloud_storage_premium_only_message', {
								default:
									'Your Premium subscription includes 1 Camera Cloud Storage slot. Subscribe to a Camera Cloud Storage plan to add more.',
							})}
						/>
					{:else}
						<Alert
							type="info"
							class="mb-m"
							heading={$format('alert_camera_cloud_storage_premium_only_heading', {
								default:
									'You have 1 Camera Cloud Storage slot included in your Premium subscription',
							})}
							message={$format('alert_camera_cloud_storage_premium_only_message', {
								default:
									'Your Premium subscription includes 1 Camera Cloud Storage slot. Subscribe to a Camera Cloud Storage plan to add more.',
							})}
						/>
					{/if}
				{/if}

				<Text styleType="body-small" class="text-uppercase text-color-muted text-bold mb-s mt-m">
					{$format('text_subscribed_cameras', { default: 'Subscribed cameras' })}
				</Text>

				<Stack gap="xs">
					{#each slots as slot (slot.index)}
						{@const slotDevice = getCameraDevice(slot.cameraId)}
						{@const slotImage = slotDevice ? getDeviceImage(slotDevice) : undefined}
						<div class="ccs-slot-row" class:ccs-slot-row--empty={!slot.cameraId}>
							{#if slot.cameraId}
								<div class="d-flex align-items-center gap-s flex-1 min-width-0">
									<div class="ccs-device-avatar">
										{#if slotImage}
											<img src={slotImage} alt={getCameraName(slot.cameraId)} />
										{:else}
											<Icon icon="fas fa-camera" />
										{/if}
									</div>
									<div class="flex-1 min-width-0">
										<Text styleType="body-small" class="text-bold">
											{getCameraName(slot.cameraId)}
										</Text>
										<div class="d-flex gap-xs mt-xs">
											{#if slot.source === 'premium'}
												{#if $showNewLayout}
													<LabelV2
														size="s"
														value={$format('label_premium_subscription', {
															default: 'Premium Subscription',
														})}
														icon="fas fa-gem"
														backgroundColor="--color-premium-10"
													/>
												{:else}
													<Label
														size="s"
														value={$format('label_premium_subscription', {
															default: 'Premium Subscription',
														})}
														icon="fas fa-gem"
														type="premium"
													/>
												{/if}
											{:else if $showNewLayout}
												<LabelV2
													size="s"
													value={$format('label_camera_subscription', {
														default: 'Camera Subscription',
													})}
													icon="fas fa-camera"
												/>
											{:else}
												<Label
													size="s"
													value={$format('label_camera_subscription', {
														default: 'Camera Subscription',
													})}
													icon="fas fa-camera"
												/>
											{/if}
										</div>
									</div>
								</div>
								<div class="ccs-slot-actions">
									<IconButton
										icon="fas fa-pen"
										aria-label={$format('label_replace_camera_in_slot', {
											default: 'Replace camera in this slot',
										})}
										on:click={() => startReplace(slot)}
									/>
									<IconButton
										icon="fas fa-trash"
										variant="danger"
										aria-label={$format('label_remove_camera_from_slot', {
											default: 'Remove camera from this slot',
										})}
										on:click={() => removeFromSlot(slot)}
									/>
								</div>
							{:else}
								<button
									type="button"
									class="ccs-empty-slot"
									on:click={() => startReplace(slot)}
									aria-label={$format('label_add_camera_to_slot', {
										default: 'Add camera to this slot',
									})}
								>
									<Icon icon="fas fa-plus" />
									<Text styleType="body-small" class="text-color-muted">
										{$format('label_empty_slot', { default: 'Empty slot' })}
									</Text>
								</button>
							{/if}
						</div>
					{/each}
				</Stack>

				{#if freeSlotsCount > 0}
					<div class="mt-m d-flex">
						<svelte:component
							this={ButtonComp}
							variant="primary"
							size="m"
							icon="fas fa-plus"
							on:click={startAdd}
						>
							{$format('button_fill_empty_camera_slots', {
								default: 'Fill the empty camera slots',
							})}
						</svelte:component>
					</div>
				{:else if premiumOnlyNoPlans}
					<div class="mt-m d-flex">
						<Button
							class="ccs-plan-button ccs-plan-button--purple"
							size="m"
							variant="secondary"
							on:click={() => dispatch('upgrade')}
						>
							{$format('button_promo_subscribe_now', {
								default: 'Subscribe now',
							})}
						</Button>
					</div>
				{/if}
			{:else if view === 'add'}
				<Container padding="0">
					<Text styleType="body-small" class="text-color-muted mb-s">
						{$format('text_select_cameras_for_slots', {
							default: `Select up to ${freeSlotsCount} camera${freeSlotsCount === 1 ? '' : 's'} to fill your free slots.`,
							values: { count: freeSlotsCount },
						})}
					</Text>
					<SelectCameraForSlotPicker
						mode="multi"
						maxSelection={freeSlotsCount}
						occupiedCameraIds={occupiedIds}
						initialSelection={[]}
						on:change={onPickerChange}
					/>
				</Container>
			{:else if view === 'replace'}
				<Container padding="0">
					<Text styleType="body-small" class="text-color-muted mb-s">
						{$format('text_replace_camera_in_slot_help', {
							default: 'Pick the camera that should occupy this slot.',
						})}
					</Text>
					<SelectCameraForSlotPicker
						mode="single"
						maxSelection={1}
						occupiedCameraIds={occupiedIds}
						initialSelection={replaceInitialSelection}
						on:change={onPickerChange}
					/>
				</Container>
			{/if}
		</div>

		<div slot="footer" class="d-flex justify-content-end gap-s">
			{#if view === 'main'}
				<svelte:component
					this={ButtonComp}
					variant="secondary"
					size="m"
					disabled={saving}
					on:click={handleClose}
				>
					{$format('button_cancel', { default: 'Cancel' })}
				</svelte:component>
				<svelte:component
					this={ButtonComp}
					variant="primary"
					size="m"
					loading={saving}
					disabled={!hasDirtyChanges || saving}
					on:click={handleSave}
				>
					{$format('button_save', { default: 'Save' })}
				</svelte:component>
			{:else}
				<svelte:component this={ButtonComp} variant="secondary" size="m" on:click={cancelPicker}>
					{$format('button_cancel', { default: 'Cancel' })}
				</svelte:component>
				<svelte:component
					this={ButtonComp}
					variant="primary"
					size="m"
					disabled={pickerSelection.length === 0}
					on:click={() => (view === 'add' ? applyAdd() : applyReplace())}
				>
					{$format('button_select_camera', { default: 'Select camera' })}
				</svelte:component>
			{/if}
		</div>
	</Modal>
{/if}

<style>
	.ccs-modal-banner {
		border-radius: 12px;
		padding: 16px;
		color: #ffffff;
		display: flex;
		align-items: center;
		gap: 12px;
		flex-wrap: wrap;
	}

	.ccs-banner-icon {
		width: 32px;
		height: 32px;
		border-radius: 8px;
		background: rgba(255, 255, 255, 0.15);
		display: inline-flex;
		align-items: center;
		justify-content: center;
		color: #ffffff;
	}

	.flex-1 {
		flex: 1;
	}

	.min-width-0 {
		min-width: 0;
	}

	:global(.text-white-imp),
	:global(.text-white-imp *) {
		color: #ffffff !important;
	}

	:global(.text-white-imp--soft),
	:global(.text-white-imp--soft *) {
		opacity: 0.85;
	}

	/*
	 * Modal's content slot is the scroll container — `removeScrollPadding`
	 * strips its default inset, which leaves the body flush with the
	 * scrollbar. A small inline-end padding restores a comfortable gap
	 * between the slot rows and the vertical scrollbar.
	 */
	.ccs-modal-content {
		padding-inline-end: 8px;
		padding-left: 15px;
		padding-right: 15px;
	}

	.ccs-slot-row {
		display: flex;
		align-items: center;
		gap: 12px;
		background: var(--color-secondary-1, #242527);
		border: 1px solid var(--color-secondary-2, #303235);
		border-radius: 12px;
		padding: 12px;
	}

	/*
	 * Square device thumbnail used in slot rows. The inner <img> fills
	 * the box and crops with `object-fit: cover` so different device
	 * artwork aspect ratios still look uniform.
	 */
	.ccs-device-avatar {
		width: 36px;
		height: 36px;
		border-radius: 8px;
		background: var(--color-secondary-2, #303235);
		display: inline-flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
		flex-shrink: 0;
	}

	.ccs-device-avatar img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.ccs-slot-row--empty {
		padding: 0;
		background: transparent;
		border: none;
	}

	.ccs-slot-actions {
		display: inline-flex;
		gap: 4px;
		flex-shrink: 0;
	}

	.ccs-empty-slot {
		display: flex;
		align-items: center;
		gap: 12px;
		width: 100%;
		background: transparent;
		border: 1px dashed var(--color-secondary-3, #5b5f66);
		border-radius: 12px;
		padding: 16px;
		color: inherit;
		cursor: pointer;
		transition: border-color 0.2s ease-in-out;
	}

	.ccs-empty-slot:hover {
		border-color: #893bf2;
	}

	.gap-xs {
		gap: 4px;
	}

	.mt-xs {
		margin-top: 4px;
	}
</style>
